import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../AppContext";
import { getParsedLocalStorageItem } from "../components/Navbar/Navbar";
import { localStorageGetItem, localStorageSetItem } from "../App";
import orderRoute from "../services/orderRoute";
import authRoute from "../services/authRoute";
import { trackAddToCart, trackAddToWishlist } from "../services/tracking";
import { trackEvent } from "../mixpanel";

import {
  getProductId,
  sanitizeProductName,
  updateAllProductCaches,
  getProductName,
  getProductPrice,
  getProductCategory,
  getProductDetailPath,
  getProductDetailUrl,
} from "../utils";
import {
  trackOmnisendAddToCart,
  trackOmnisendProductViewed,
  trackOmnisendAddedToWishlist,
  trackOmnisendCategoryViewed,
  trackOmnisendBoxesViewed,
} from "../omnisend";

const getTrackedProductUrl = (product) => {
  const detailUrl = getProductDetailUrl(product);
  if (detailUrl) {
    return detailUrl;
  }
  const sanitizedProductName = sanitizeProductName(getProductName(product));
  const productId = product?.sku;
  if (!sanitizedProductName || !productId) {
    return "";
  }
  return `${window.location.origin}/products/${sanitizedProductName}/${productId}`;
};

// Hook for page visit tracking
export const usePageTracking = (pageName) => {
  const { trackVisit } = authRoute();
  const { showToast } = useAppContext();
  const userData = getParsedLocalStorageItem("userData");

  useEffect(() => {
    const sessionKey = `track${pageName.replace(/\s+/g, "")}`;
    const hasSeenModal = sessionStorage.getItem(sessionKey) === "true";

    if (!hasSeenModal) {
      sessionStorage.setItem(sessionKey, "true");
      const body = {
        user_id: userData?.id || "Guest",
        path: pageName,
      };
      trackVisit(body, showToast);
    }
  }, [pageName, userData]);
};

// Hook for wishlist management
export const useWishlistManager = (products = []) => {
  const { showToast, setWishlistItems, apiWishlistItems, setApiWishlistItems } =
    useAppContext();

  const { updateWishlist, getWishlist } = orderRoute();
  const [loading, setLoading] = useState(false);
  const [filledHearts, setFilledHearts] = useState({});

  const [pendingHearts, setPendingHearts] = useState({});
  const pendingHeartsRef = useRef({});

  const userData = getParsedLocalStorageItem("userData");
  const userToken = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!userToken;

  // Fetch wishlist on mount if logged in
  useEffect(() => {
    if (isLoggedIn) {
      getWishlist(setLoading, showToast, setApiWishlistItems);
    }
  }, [isLoggedIn]);

  // Create a stable reference for products based on their IDs
  const productIds = useMemo(
    () => products.map((p) => getProductId(p)).join(","),
    [products],
  );

  // Update wishlist status when products or wishlist changes
  useEffect(() => {
    let wishlist = [];

    if (isLoggedIn) {
      wishlist = apiWishlistItems;
    } else {
      wishlist = JSON.parse(localStorageGetItem("wishlistItems")) || [];
    }

    const updatedWishlistStatus = {};
    products.forEach((product) => {
      const productId = getProductId(product);
      const isInWishlist = wishlist.some(
        (wishlistItem) =>
          (wishlistItem?.product?.id || wishlistItem?.product?._id) ===
            productId ||
          (wishlistItem?.box?.id || wishlistItem?.box?._id) === productId,
      );
      updatedWishlistStatus[productId] = isInWishlist;
    });
    setFilledHearts(updatedWishlistStatus);
  }, [productIds, isLoggedIn, apiWishlistItems]);

  const toggleFill = useCallback(
    (productId) => {
      const product = products.find((p) => p.id === productId);

      if (isLoggedIn) {
        if (pendingHeartsRef.current[productId]) return;
        pendingHeartsRef.current[productId] = true;
        setFilledHearts((prev) => ({
          ...prev,
          [productId]: !prev[productId],
        }));
        setPendingHearts((prev) => ({ ...prev, [productId]: true }));
        Promise.resolve(
          updateWishlist(
            productId,
            showToast,
            filledHearts,
            () => {},
            apiWishlistItems,
            setApiWishlistItems,
            products,
          ),
        ).finally(() => {
          delete pendingHeartsRef.current[productId];
          setPendingHearts((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        });
      } else {
        const newState = !filledHearts[productId];
        setFilledHearts((prevState) => ({
          ...prevState,
          [productId]: newState,
        }));

        let wishlist = getParsedLocalStorageItem("wishlistItems") || [];
        const index = wishlist.findIndex(
          (item) =>
            (item?.product?.id || item?.product?._id) === productId ||
            (item?.box?.id || item?.box?._id) === productId,
        );
        let toastText = "";

        if (newState) {
          if (index === -1) {
            const productToAdd = products.find(
              (product) => getProductId(product) === productId,
            );

            // Determine if it's a box or product based on properties
            const isBox =
              productToAdd?.boxImage !== undefined ||
              productToAdd?.source === "box";

            wishlist = isBox
              ? [{ box: productToAdd }, ...wishlist]
              : [{ product: productToAdd }, ...wishlist];
            toastText = "Item added to Wishlist!";
          }
        } else {
          if (index !== -1) {
            wishlist = wishlist.filter(
              (item) =>
                (item?.product?.id || item?.product?._id) !== productId &&
                (item?.box?.id || item?.box?._id) !== productId,
            );
            toastText = "Item removed from Wishlist!";
          }
        }

        localStorageSetItem("wishlistItems", JSON.stringify(wishlist));
        setWishlistItems(wishlist);
        showToast({ toastText, isSuccess: true });

        trackEvent(
          newState
            ? `${getProductName(product)} Added to Wishlist`
            : `${getProductName(product)} Removed from Wishlist`,
          {
            "Product ID": productId,
            "Product Name": getProductName(product),
            "Category Name": getProductCategory(product),
            User: userData?.email || "Anonymous",
          },
        );

        trackAddToWishlist(
          productId,
          getProductName(product),
          newState ? "Add to Wishlist" : "Remove from Wishlist",
          userData,
        );
        if (newState && userData?.email) {
          const productUrl = product?.boxImage
            ? `${window.location.origin}/foodbundles`
            : getTrackedProductUrl(product);
          trackOmnisendAddedToWishlist(userData?.email, {
            id: getProductId(product),
            title: getProductName(product),
            price: getProductPrice(product),
            image: product.productImage || product.boxImage,
            url: productUrl,
          });
        }
      }
    },
    [
      products,
      isLoggedIn,
      filledHearts,
      apiWishlistItems,
      userData,
      showToast,
      setWishlistItems,
      updateWishlist,
      setApiWishlistItems,
    ],
  );

  const isInWishlist = useCallback((id) => !!filledHearts[id], [filledHearts]);
  const isWishlistPending = useCallback(
    (id) => !!pendingHearts[id],
    [pendingHearts],
  );

  return { filledHearts, toggleFill, isInWishlist, isWishlistPending, loading };
};

// Hook for cart management
export const useCartManager = (
  productSources = [],
  additionalSources = [],
  location = "Page",
) => {
  const { showToast, cartItems, setCartItems, updateCart } = useAppContext();
  const queryClient = useQueryClient();
  const userData = getParsedLocalStorageItem("userData");

  const allProducts = [
    ...(Array.isArray(productSources) ? productSources : []),
    ...(Array.isArray(additionalSources) ? additionalSources : []),
  ].flat();

  const handleIncreaseQuantity = useCallback(
    (id, increment = 1, replace = false) => {
      const product = allProducts.find((p) => getProductId(p) === id);

      if (!product) {
        console.warn(`Product ${id} not found`);
        return;
      }

      const normalizedId = getProductId(product);
      const productNameWithSize = getProductName(product);
      const user = userData?.email || "Anonymous";

      let computedCart = null;

      setCartItems((prevCart) => {
        const cartIndex = prevCart.findIndex(
          (i) => getProductId(i) === normalizedId,
        );
        const currentQty = cartIndex !== -1 ? prevCart[cartIndex].quantity : 0;
        const available = product.quantityAvailable ?? Infinity;
        const newQty = replace ? increment : currentQty + increment;

        if (newQty > available) {
          showToast({
            toastText: "This product has limited stock!",
            isSuccess: false,
          });
          return prevCart;
        }

        let newCart;
        if (cartIndex !== -1) {
          newCart = prevCart.map((item, idx) =>
            idx === cartIndex ? { ...item, quantity: newQty } : item,
          );

          trackEvent(`${productNameWithSize} Increased in Cart`, {
            "Product ID": normalizedId,
            "Product Name": productNameWithSize,
            "Category Name": getProductCategory(product),
            "New Quantity": newQty,
            User: user,
            Location: location,
          });
          if (userData?.email) {
            const productUrl = product?.boxImage
              ? `${window.location.origin}/foodbundles`
              : getTrackedProductUrl(product);
            trackOmnisendAddToCart(userData?.email, {
              id: normalizedId,
              title: productNameWithSize,
              price: getProductPrice(product),
              image: product.productImage || product.boxImage,
              quantity: newQty,
              url: productUrl,
            });
          }
          trackAddToCart(
            normalizedId,
            productNameWithSize,
            getProductPrice(product),
            "Increase Quantity in Cart",
            userData,
          );
        } else {
          const newItem = { ...product, id: normalizedId, quantity: increment };
          newCart = [newItem, ...prevCart];
          trackEvent(`Add to Cart`, {
            "Product ID": normalizedId,
            "Product Name": productNameWithSize,
            "Category Name": getProductCategory(product),
            "Quantity Added": increment,
            User: user,
            Location: location,
          });
          if (userData?.email) {
            const productUrl = product?.boxImage
              ? `${window.location.origin}/foodbundles`
              : getTrackedProductUrl(product);
            trackOmnisendAddToCart(userData?.email, {
              id: normalizedId,
              title: productNameWithSize,
              price: getProductPrice(product),
              image: product.productImage || product.boxImage,
              quantity: increment,
              url: productUrl,
            });
          }
          trackAddToCart(
            normalizedId,
            productNameWithSize,
            getProductPrice(product),
            "Add to Cart",
            userData,
          );
        }

        updateAllProductCaches(queryClient, normalizedId, (prevQty = 0) =>
          replace ? increment : prevQty + increment,
        );

        computedCart = newCart;
        return newCart;
      });

      // Sync localStorage after state update (outside the updater to avoid double setState)
      if (computedCart !== null) {
        updateCart(computedCart);
      }
    },
    [allProducts, queryClient, userData, showToast, updateCart, location],
  );

  const handleDecreaseQuantity = useCallback(
    (id) => {
      const normalizedId = id;

      let computedCart = null;

      setCartItems((prevCart) => {
        const cartIndex = prevCart.findIndex(
          (i) => getProductId(i) === normalizedId,
        );
        if (cartIndex === -1) return prevCart;

        const item = prevCart[cartIndex];
        const productNameWithSize = getProductName(item);
        const user = userData?.email || "Anonymous";

        let newCart;
        let newQty;

        if (item.quantity > 1) {
          newQty = item.quantity - 1;
          newCart = prevCart.map((i, idx) =>
            idx === cartIndex ? { ...i, quantity: newQty } : i,
          );

          trackEvent(`${productNameWithSize} Quantity Decreased in Cart`, {
            "Product ID": normalizedId,
            "Product Name": productNameWithSize,
            "Category Name": getProductCategory(item),
            "New Quantity": newQty,
            User: user,
            Location: location,
          });

          trackAddToCart(
            normalizedId,
            productNameWithSize,
            getProductPrice(item),
            "Decrease Quantity in Cart",
            userData,
          );
        } else {
          newQty = 0;
          newCart = prevCart.filter((_, idx) => idx !== cartIndex);

          trackEvent(`Removed from Cart`, {
            "Product ID": normalizedId,
            "Product Name": productNameWithSize,
            "Category Name": item.category,
            User: user,
            Location: location,
          });

          trackAddToCart(
            normalizedId,
            productNameWithSize,
            item.price,
            "Remove from Cart",
            userData,
          );
        }

        updateAllProductCaches(queryClient, normalizedId, () => newQty);
        computedCart = newCart;
        return newCart;
      });

      // Sync localStorage after state update (outside the updater to avoid double setState)
      if (computedCart !== null) {
        updateCart(computedCart);
      }
    },
    [queryClient, userData, updateCart, location],
  );

  return { handleIncreaseQuantity, handleDecreaseQuantity };
};

// Hook for product navigation
export const useProductNavigation = () => {
  const navigate = useNavigate();
  const userData = getParsedLocalStorageItem("userData");

  const handleShowProductModal = useCallback(
    (product) => {
      const productPath = getProductDetailPath(product);
      const fallbackPath = `/products/${sanitizeProductName(
        getProductName(product),
      )}/${product?.sku}`;
      const navigationPath = productPath || fallbackPath;
      const productUrl =
        getProductDetailUrl(product) ||
        `${window.location.origin}${navigationPath}`;
      trackEvent(`Product Viewed`, {
        "Product ID": product?.id,
        "Product Name": getProductName(product),
        "Category Name": getProductCategory(product),
        "Product URL": productUrl,
        User: userData?.email || "Anonymous",
      });
      navigate(navigationPath);
    },
    [navigate, userData],
  );

  const handleShowBoxModal = useCallback(
    (boxId, setSelectedBoxId, setShowModal) => {
      setSelectedBoxId(boxId);
      setShowModal(true);

      // We still need the box object for tracking if available,
      // but the modal now fetches its own data.
      // For simplicity in this common hook, we might just pass box object for tracking and extract ID.
    },
    [userData],
  );

  const encodeCategoryName = (name) => {
    return encodeURIComponent(name.replace(/&/g, "and").replace(/\s+/g, "-"));
  };

  const handleCategoryChange = useCallback(
    (category) => {
      const categoryName = encodeCategoryName(category.categoryName);
      const categoryId = category.sku;

      trackEvent(`${category.categoryName} Category Viewed`, {
        "Category Name": category.categoryName,
        User: userData?.email || "Anonymous",
      });
      if (userData?.email) {
        trackOmnisendCategoryViewed(userData?.email, {
          id: category?.id,
          title: category?.categoryName,
          image: category?.categoryImage,
          url: `${window.location.origin}/category/${categoryName}/${categoryId}`,
        });
      }
      navigate(`/category/${categoryName}/${categoryId}`);
    },
    [navigate, userData],
  );

  return { handleShowProductModal, handleShowBoxModal, handleCategoryChange };
};

// Utility hook for margin bottom calculations
export const useMarginBottomStyle = (length) => {
  switch (length) {
    case 0:
      return { marginBottom: "305px" };
    case 1:
    case 2:
      return { marginBottom: "290px" };
    case 3:
      return { marginBottom: "80px" };
    case 4:
      return { marginBottom: "60px" };
    default:
      return { marginBottom: "100px" };
  }
};
